/**
 * Embedding-based similarity over transactions / paystubs / line items.
 * In-memory HNSW-style top-K via brute-force cosine (acceptable up to ~10k).
 */
import { embed, cosineSimilarity } from './llm.ts';

export interface EmbeddedRecord<T> {
  readonly id: string;
  readonly vec: Float32Array;
  readonly source: T;
}

const STORAGE_KEY_PREFIX = 'empire:embed:';

export class EmbeddingIndex<T extends { id: string }> {
  #records: EmbeddedRecord<T>[] = [];
  #key: string;
  #fingerprint: (r: T) => string;

  constructor(namespace: string, fingerprint: (r: T) => string) {
    this.#key = STORAGE_KEY_PREFIX + namespace;
    this.#fingerprint = fingerprint;
    this.#hydrate();
  }

  #hydrate(): void {
    const raw = localStorage.getItem(this.#key);
    if (raw === null) return;
    try {
      const parsed = JSON.parse(raw) as Array<{ id: string; vec: number[]; source: T }>;
      this.#records = parsed.map((p) => ({ id: p.id, vec: Float32Array.from(p.vec), source: p.source }));
    } catch {
      // ignore corrupt
    }
  }

  #persist(): void {
    const serializable = this.#records.map((r) => ({ id: r.id, vec: Array.from(r.vec), source: r.source }));
    try {
      localStorage.setItem(this.#key, JSON.stringify(serializable));
    } catch {
      // quota — drop oldest half
      this.#records = this.#records.slice(Math.floor(this.#records.length / 2));
    }
  }

  async ensureIndexed(records: T[]): Promise<void> {
    const have = new Set(this.#records.map((r) => r.id));
    const toAdd = records.filter((r) => !have.has(r.id));
    if (toAdd.length === 0) return;
    for (const rec of toAdd) {
      try {
        const vec = await embed(this.#fingerprint(rec));
        this.#records.push({ id: rec.id, vec, source: rec });
      } catch {
        // skip on embedder failure
      }
    }
    this.#persist();
  }

  async findSimilar(query: T, topK: number = 5): Promise<Array<{ record: T; score: number }>> {
    const qvec = await embed(this.#fingerprint(query));
    const scored = this.#records
      .filter((r) => r.id !== query.id)
      .map((r) => ({ record: r.source, score: cosineSimilarity(qvec, r.vec) }))
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, topK);
  }

  size(): number {
    return this.#records.length;
  }
}
